import {
	Checkbox,
	CheckboxGroup,
	FormControl,
	FormHelperText,
	FormLabel,
	Input,
	InputProps,
	NumberInput,
	NumberInputField,
	NumberInputProps,
	Select,
	SelectProps,
	Stack,
	Switch,
	SwitchProps,
	Textarea,
	TextareaProps,
	useColorModeValue,
} from '@chakra-ui/react';
import { Select as ChakraSelect, CreatableSelect } from 'chakra-react-select';
import { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { typography } from 'theme/tokens';
import FormErrorMessageWithSpacing from '../form-error-message';
import FileUploadControl from './FileUploadControl';
import { parsePrice } from '../../utils/parse-price';

// ---------------------------------------------------------------------------
// Shared props across all field variants
// ---------------------------------------------------------------------------
interface BaseFieldProps {
	/** Field label displayed above the control */
	label: string;
	/** Short helper text below the control */
	helperText?: string;
	/** Error message — shows when truthy and marks the field as invalid */
	error?: string;
	/** Makes the label show a required asterisk */
	isRequired?: boolean;
	/** Disables the field */
	isDisabled?: boolean;
}

// ---------------------------------------------------------------------------
// Variant-specific prop bundles
// ---------------------------------------------------------------------------
interface TextFieldBaseProps extends BaseFieldProps {
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	inputProps?: Omit<InputProps, 'value' | 'onChange'>;
	/** Date inputs: minimum date string (YYYY-MM-DD) */
	min?: string;
	/** Date inputs: maximum date string (YYYY-MM-DD) */
	max?: string;
}

interface TextFieldProps extends TextFieldBaseProps {
	type: 'text' | 'email' | 'password' | 'url' | 'tel' | 'date' | 'datetime-local' | 'time';
}

/** When type is omitted, defaults to text — this variant makes the union discriminate correctly */
interface DefaultTextFieldProps extends TextFieldBaseProps {
	type?: undefined;
}

interface NumberFieldProps extends BaseFieldProps {
	type: 'number';
	value: number | string;
	onChange: (value: number) => void;
	/** Fired when the field is cleared to empty — lets optional numbers be unset. */
	onEmpty?: () => void;
	placeholder?: string;
	min?: number;
	max?: number;
	step?: number;
	numberInputProps?: Omit<NumberInputProps, 'value' | 'onChange'>;
}

interface SelectFieldProps extends BaseFieldProps {
	type: 'select';
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	options: { value: string; label: string }[];
	selectProps?: Omit<SelectProps, 'value' | 'onChange'>;
}

interface SwitchFieldProps extends BaseFieldProps {
	type: 'switch';
	isChecked: boolean;
	onChange: (checked: boolean) => void;
	switchProps?: Omit<SwitchProps, 'isChecked' | 'onChange'>;
}

interface TextareaFieldProps extends BaseFieldProps {
	type: 'textarea';
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	textareaProps?: Omit<TextareaProps, 'value' | 'onChange'>;
}

interface CheckboxGroupFieldProps extends BaseFieldProps {
	type: 'checkbox-group';
	value: string[];
	onChange: (value: string[]) => void;
	options: { value: string; label: string }[];
}

interface DatePickerFieldProps extends BaseFieldProps {
	type: 'date-picker';
	value: Date | null;
	onChange: (date: Date | null) => void;
	placeholder?: string;
	/** Show time selection alongside date */
	showTimeSelect?: boolean;
	/** Time interval in minutes (default 15) */
	timeIntervals?: number;
	/** Minimum selectable date */
	minDate?: Date;
	/** Maximum selectable date */
	maxDate?: Date;
	/** Date format string (default "Pp" for date+time, "P" for date only) */
	dateFormat?: string;
	/** Show clear button */
	isClearable?: boolean;
}

interface MultiSelectFieldProps extends BaseFieldProps {
	type: 'multi-select';
	value: string[];
	onChange: (values: string[]) => void;
	options: { value: string; label: string }[];
	placeholder?: string;
	/** Allow creating new options on the fly */
	isCreatable?: boolean;
	/** Close menu after selecting (default false for multi) */
	closeMenuOnSelect?: boolean;
}

interface FileFieldProps extends BaseFieldProps {
	type: 'file';
	value: File | null;
	onChange: (file: File | null) => void;
	/** Accepted MIME types (e.g. 'image/*', '.csv') */
	accept?: string;
	/** Show image preview for image files */
	showPreview?: boolean;
	/** Max file size in bytes */
	maxSize?: number;
}

export type FormFieldProps =
	| DefaultTextFieldProps
	| TextFieldProps
	| NumberFieldProps
	| SelectFieldProps
	| SwitchFieldProps
	| TextareaFieldProps
	| CheckboxGroupFieldProps
	| DatePickerFieldProps
	| MultiSelectFieldProps
	| FileFieldProps;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function FormField(props: FormFieldProps) {
	const { label, helperText, error, isRequired, isDisabled } = props;
	const labelColor = useColorModeValue('secondaryGray.900', 'white');
	const helperColor = useColorModeValue('secondaryGray.500', 'gray.400');

	const isInvalid = Boolean(error);

	return (
		<FormControl isInvalid={isInvalid} isRequired={isRequired} isDisabled={isDisabled}>
			<FormLabel {...typography.formLabel} color={labelColor} mb="6px">
				{label}
			</FormLabel>

			{renderControl(props)}

			{helperText && !isInvalid && (
				<FormHelperText fontSize="xs" color={helperColor} mt="4px">
					{helperText}
				</FormHelperText>
			)}
			{isInvalid && <FormErrorMessageWithSpacing title={error!} />}
		</FormControl>
	);
}

// ---------------------------------------------------------------------------
// Control renderer
// ---------------------------------------------------------------------------
function renderControl(props: FormFieldProps) {
	const type = ('type' in props && props.type) || 'text';

	switch (type) {
		case 'switch': {
			const p = props as SwitchFieldProps;
			return (
				<Switch
					colorScheme="brandScheme"
					size="md"
					isChecked={p.isChecked}
					onChange={(e) => p.onChange(e.target.checked)}
					{...p.switchProps}
				/>
			);
		}

		case 'select': {
			const p = props as SelectFieldProps;
			return (
				<Select
					variant="main"
					value={p.value}
					placeholder={p.placeholder}
					onChange={(e) => p.onChange(e.target.value)}
					{...p.selectProps}
				>
					{p.options.map((opt) => (
						<option key={opt.value} value={opt.value}>
							{opt.label}
						</option>
					))}
				</Select>
			);
		}

		case 'number': {
			const p = props as NumberFieldProps;
			return <NumberFieldControl {...p} />;
		}

		case 'textarea': {
			const p = props as TextareaFieldProps;
			return (
				<Textarea
					variant="main"
					value={p.value}
					placeholder={p.placeholder}
					onChange={(e) => p.onChange(e.target.value)}
					{...p.textareaProps}
				/>
			);
		}

		case 'checkbox-group': {
			const p = props as CheckboxGroupFieldProps;
			return (
				<CheckboxGroup
					value={p.value}
					onChange={(values) => p.onChange(values as string[])}
				>
					<Stack spacing={2}>
						{p.options.map((opt) => (
							<Checkbox key={opt.value} value={opt.value} colorScheme="brandScheme">
								{opt.label}
							</Checkbox>
						))}
					</Stack>
				</CheckboxGroup>
			);
		}

		case 'date-picker': {
			const p = props as DatePickerFieldProps;
			return (
				<DatePicker
					selected={p.value}
					onChange={p.onChange}
					showTimeSelect={p.showTimeSelect ?? false}
					timeIntervals={p.timeIntervals ?? 15}
					dateFormat={p.dateFormat ?? (p.showTimeSelect ? 'Pp' : 'P')}
					placeholderText={p.placeholder}
					wrapperClassName="date-picker-wrapper"
					className="date-picker-input"
					minDate={p.minDate}
					maxDate={p.maxDate}
					isClearable={p.isClearable ?? true}
				/>
			);
		}

		case 'multi-select': {
			const p = props as MultiSelectFieldProps;
			const SelectComponent = p.isCreatable ? CreatableSelect : ChakraSelect;
			// Portal the menu to the body so it isn't clipped by a parent card's
			// overflow (e.g. PageCard's overflowY:hidden).
			const menuPortalTarget = typeof document !== 'undefined' ? document.body : undefined;
			return (
				<SelectComponent
					isMulti
					value={p.options.filter((opt) => p.value.includes(opt.value))}
					onChange={(selected: any) => p.onChange(selected?.map((s: any) => s.value) ?? [])}
					options={p.options}
					placeholder={p.placeholder}
					closeMenuOnSelect={p.closeMenuOnSelect ?? false}
					menuPortalTarget={menuPortalTarget}
					menuPosition="fixed"
					styles={{ menuPortal: (base) => ({ ...base, zIndex: 1500 }) }}
					chakraStyles={{
						container: (base: any) => ({ ...base, w: '100%' }),
						control: (base: any) => ({
							...base,
							borderRadius: '12px',
							borderColor: 'secondaryGray.100',
							bg: 'gray.50',
							_focus: { borderColor: 'brand.500', boxShadow: '0 0 0 2px rgba(2, 63, 127, 0.15)' },
						}),
					}}
					tagColorScheme="brand"
				/>
			);
		}

		case 'file': {
			const p = props as FileFieldProps;
			return (
				<FileUploadControl
					value={p.value}
					onChange={p.onChange}
					accept={p.accept}
					showPreview={p.showPreview}
					maxSize={p.maxSize}
				/>
			);
		}

		default: {
			const p = props as TextFieldProps;
			return (
				<Input
					variant="main"
					type={p.type || 'text'}
					value={p.value}
					placeholder={p.placeholder}
					min={p.min}
					max={p.max}
					onChange={(e) => p.onChange(e.target.value)}
					{...p.inputProps}
				/>
			);
		}
	}
}

// ---------------------------------------------------------------------------
// NumberFieldControl — uses local string state so intermediate decimal input
// like "12." isn't stripped by the parent converting to number immediately.
// ---------------------------------------------------------------------------
export function NumberFieldControl(p: NumberFieldProps) {
	const [localValue, setLocalValue] = useState(String(p.value ?? ''));

	useEffect(() => {
		const numLocal = parsePrice(localValue);
		const numProp = typeof p.value === 'number' ? p.value : parsePrice(String(p.value));
		// Don't reset if the local string already represents the same number
		if (!isNaN(numLocal) && !isNaN(numProp) && numLocal === numProp) return;
		if (localValue === String(p.value)) return;
		setLocalValue(String(p.value ?? ''));
	}, [p.value]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<NumberInput
			variant="main"
			value={localValue}
			min={p.min}
			max={p.max}
			step={p.step}
			onChange={(valueAsString) => {
				setLocalValue(valueAsString);
				if (valueAsString === '') {
					p.onEmpty?.();
					return;
				}
				const parsed = parsePrice(valueAsString);
				if (!isNaN(parsed)) {
					p.onChange(parsed);
				}
			}}
			{...p.numberInputProps}
		>
			<NumberInputField placeholder={p.placeholder} />
		</NumberInput>
	);
}
